export const norm = (r) => (r && typeof r === "object" && !Array.isArray(r) && "data" in r ? r.data : r);

export const parseListResponse = (res) => {
  const rows = res && typeof res === "object" && !Array.isArray(res) && "data" in res ? res.data : res;
  const arr = Array.isArray(rows) ? rows : [];
  const total = typeof res?.total === "number" ? res.total : arr.length;
  return { data: arr, total };
};

export const KANBAN_PAGE_SIZE = 40;
export const LIST_PAGE_SIZE = 80;

/** Odoo-like palette */
export const ODOO = {
  primary: "#714B67",
  primaryHover: "#5d3d55",
  primarySoft: "#f3eef2",
  border: "#dee2e6",
  borderLight: "#e9ecef",
  text: "#212529",
  muted: "#6c757d",
  controlBg: "#ffffff",
  boardBg: "#f8f9fa",
  columnBg: "#f0f0f0",
  cardBorder: "#ced4da",
  footerBg: "#714B67",
  link: "#008784",
  star: "#f3cc00",
  tagBlue: "#00A09D",
  tagOrange: "#f0ad4e",
};

export const AVATAR_BG = [
  "#4c6a4f", "#5b6b3f", "#3f6b63", "#6b5b3f", "#4f5b6b", "#6b4f5b",
  "#5a6b3f", "#3f5b6b", "#6b6b4f", "#4f6a6b", "#6b4f6b", "#5b4f6b",
];

export const getInitials = (n) =>
  (n || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

export const priorityStars = (priority) => {
  const p = Number(priority) || 0;
  // Odoo: priority 0–3 → 0–3 filled stars (out of 3)
  return Math.min(3, Math.max(0, p));
};

export const stageBarColor = (stage) => {
  if (stage?.is_won) return "#28a745";
  if (stage?.is_lost) return "#dc3545";
  const name = String(stage?.name || "").toLowerCase();
  if (name.includes("négoc") || name.includes("negoc")) return "#017e84";
  if (name.includes("qualif")) return "#00A09D";
  if (name.includes("prospect") || name.includes("suspect")) return "#f0ad4e";
  return "#adb5bd";
};

export const tagDotColor = (label) => {
  const n = String(label || "").toLowerCase();
  if (n.includes("négoc") || n.includes("negoc")) return "#017e84";
  if (n.includes("prospect") || n.includes("suspect")) return "#f0ad4e";
  if (n.includes("qualif")) return "#00A09D";
  return "#6c757d";
};

// ─── Extras persistance via `description` ───────────────────────────────────
// Les champs maquette Odoo (category, vehicle_type, address, etc.) n'existent
// pas côté API. On les sérialise dans `description` via un marqueur HTML comment
// invisible côté UI mais réHydratable au rechargement.
// Format: "<clean notes>\n\n<!--CRM_EXTRAS:{...json}-->"
const EXTRAS_START = "<!--CRM_EXTRAS:";
const EXTRAS_END = "-->";

export const parseExtrasFromDescription = (description) => {
  const raw = String(description || "");
  const start = raw.indexOf(EXTRAS_START);
  const end = raw.indexOf(EXTRAS_END, start >= 0 ? start : 0);
  if (start === -1 || end === -1) return { cleanDescription: raw, extras: null };
  const jsonStr = raw.slice(start + EXTRAS_START.length, end);
  try {
    const extras = JSON.parse(jsonStr);
    const cleanDescription = (raw.slice(0, start).trimEnd() + raw.slice(end + EXTRAS_END.length)).trim();
    return { cleanDescription, extras };
  } catch {
    return { cleanDescription: raw, extras: null };
  }
};

export const serializeExtrasToDescription = (cleanDescription, extras) => {
  const base = String(cleanDescription || "").trim();
  const hasExtras = extras && typeof extras === "object" && Object.keys(extras).some((k) => {
    const v = extras[k];
    if (Array.isArray(v)) return v.length > 0;
    return v !== "" && v !== null && v !== undefined;
  });
  if (!hasExtras) return base;
  const payload = JSON.stringify(extras);
  return base ? `${base}\n\n${EXTRAS_START}${payload}${EXTRAS_END}` : `${EXTRAS_START}${payload}${EXTRAS_END}`;
};

// Helper pour hydrater un lead: split description + merge extras à la racine
export const hydrateLeadExtras = (lead) => {
  if (!lead || typeof lead !== "object") return lead;
  const { cleanDescription, extras } = parseExtrasFromDescription(lead.description);
  if (!extras) return { ...lead, _cleanDescription: lead.description || "" };
  return {
    ...lead,
    description: cleanDescription,
    _cleanDescription: cleanDescription,
    _extrasRaw: extras,
    // expose extras keys à plat pour compatibilité (extraFields / extraInfo / properties)
    category: extras.category ?? lead.category ?? "",
    type_detail: extras.type_detail ?? lead.type_detail ?? "",
    vehicle_type: extras.vehicle_type ?? lead.vehicle_type ?? "",
    plate_number: extras.plate_number ?? lead.plate_number ?? "",
    contact_name: extras.contact_name ?? lead.contact_name ?? "",
    contact_title: extras.contact_title ?? lead.contact_title ?? "",
    street: extras.street ?? lead.street ?? "",
    address: extras.address ?? lead.address ?? "",
    job_position: extras.job_position ?? lead.job_position ?? "",
    city: extras.city ?? lead.city ?? "",
    state: extras.state ?? lead.state ?? "",
    zip: extras.zip ?? lead.zip ?? "",
    country: extras.country ?? lead.country ?? "",
    website: extras.website ?? lead.website ?? "",
    language: extras.language ?? lead.language ?? "",
    referred_by: extras.referred_by ?? lead.referred_by ?? "",
    properties: Array.isArray(extras.properties) ? extras.properties : lead.properties || [],
    // compat: certains champs viennent de extraInfo
    company_name: extras.company_name ?? lead.company_name,
    campaign_name: extras.campaign_name ?? lead.campaign_name,
    medium_name: extras.medium_name ?? lead.medium_name,
    source_name: extras.source_name ?? lead.source_name,
  };
};
